import { Link, Navigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../../icons";
import { PRODUCT_BY_SLUG, type ProductSlug } from "../../products";

export function ProductDetailPage() {
  const { t } = useTranslation("public");
  const { slug } = useParams<{ slug: string }>();
  const validSlugs: ProductSlug[] = ["secure", "driver", "delivery", "clerk", "border"];
  if (!slug || !validSlugs.includes(slug as ProductSlug)) {
    return <Navigate to="/" replace />;
  }
  const product = PRODUCT_BY_SLUG[slug as ProductSlug];
  const bullets = t(`products.${product.slug}.bullets`, { returnObjects: true }) as string[];
  const blockers = t(`products.${product.slug}.blockers`, { returnObjects: true }) as string[];

  return (
    <div className="landing">
      <header className="landing-nav">
        <Link to="/" className="brand" aria-label="AICE">
          <img src="/logo-mark.png" alt="AICE" className="brand-logo" />
        </Link>
        <nav>
          <Link to="/#cozumler">{t("nav.solutions")}</Link>
          <Link to="/#moduller">{t("nav.modules")}</Link>
          <Link to="/#neden">{t("nav.why")}</Link>
          <Link to="/#demo" className="cta">
            {t("nav.demo")}
          </Link>
        </nav>
      </header>

      <section className={`product-hero tone-${product.tone}`}>
        <div className="product-hero-inner">
          <Link to="/#cozumler" className="back-link">
            <span aria-hidden>&larr;</span> {t("nav.allSolutions")}
          </Link>
          <div className="product-hero-icon">
            <Icon name={product.icon} size={48} />
          </div>
          <span className="eyebrow">{product.brand}</span>
          <h1>{t(`products.${product.slug}.tagline`)}</h1>
          <p className="lead">{t(`products.${product.slug}.hero`)}</p>
          <div className={`status-pill status-${product.status}`}>
            <span className="status-dot" /> {t(`productDetail.status.${product.status}`)}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="product-detail-grid">
          <div>
            <h2>{t("productDetail.whatItDoes")}</h2>
            <ul className="checklist">
              {bullets.map((b) => (
                <li key={b}>
                  <Icon name="check" size={20} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h2>{t("productDetail.technicalModules")}</h2>
            <div className="module-tags">
              {product.modules.map((m) => (
                <span key={m} className="module-tag">
                  <Icon name="cpu" size={14} />
                  {m}
                </span>
              ))}
            </div>
            {blockers.length > 0 && (
              <>
                <h3 className="mt-2">{t("productDetail.thingsToKnow")}</h3>
                <ul className="blocker-list">
                  {blockers.map((b) => (
                    <li key={b}>
                      <Icon name="alert" size={16} />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <p className="muted small">{t("productDetail.transparency")}</p>
              </>
            )}
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="cta-inner">
          <div>
            <h2>{t("productDetail.ctaTitle", { brand: product.brand })}</h2>
            <p>{t("productDetail.ctaBody")}</p>
          </div>
          <Link to="/#demo" className="btn primary lg">
            {t("nav.demo")} <span aria-hidden>&rarr;</span>
          </Link>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="footer-bottom">
          <span>&copy; {new Date().getFullYear()} AiCruise Eye</span>
          <div className="footer-legal">
            <Link to="/legal/kvkk">KVKK</Link>
            <Link to="/legal/cookies">{t("legal.cookiesTitle")}</Link>
            <Link to="/legal/privacy">{t("legal.privacyTitle")}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
